import A from 'components/atoms/A/A';
import Alert from 'components/atoms/Alert/Alert';
import Button from 'components/atoms/Button/Button';
import LoadingOverlay from 'components/atoms/LoadingOverlay/LoadingOverlay';
import RegistrationCard from 'components/atoms/RegistrationCard/RegistrationCard';
import PasswordInput, {
  PASSWORD_REQUIREMENT_DESCRIPTIONS,
} from 'components/atoms/TextInput/PasswordInput';
import SecondFactorCode from 'components/atoms/TextInput/SecondFactorCode';
import UsernameInput from 'components/atoms/TextInput/UsernameInput';
import TurnstileWidget from 'components/atoms/TurnstileWidget/TurnstileWidget';
import Txt from 'components/atoms/Txt/Txt';
import Form from 'components/molecules/Form/Form';
import { DASHBOARD, REQUEST_PASSWORD_RESET } from 'components/pages/pages';
import { useLogin } from 'components/templates/AuthenticationProvider/AuthenticationProvider';
import { SecondFactorRequiredError } from 'components/templates/AuthenticationProvider/SecondFactorRequiredError';
import { useCountdownTimer } from 'hooks/useCountdownTimer';
import usePromiseState from 'hooks/usePromiseState';
import useQueryStringParams from 'hooks/useQueryStringParams';
import useRecaptcha, { RecaptchaProvider } from 'hooks/useRecaptcha';
import router, { useRouter } from 'next/router';
import React, { useState, useEffect, useCallback } from 'react';
import classes from './LoginForm.module.scss';

const LOGIN_ERROR_MAP: Record<
  string,
  { title: string; message: string; action?: React.ReactNode }
> = {
  'email/phone number or password entered is incorrect': {
    title: 'Email/Password Combination is incorrect',
    message: 'Please check the details and try again.',
    action: (
      <Button
        buttonType="text"
        className={classes.errorAlertAction}
        onClick={() => {
          router.push(REQUEST_PASSWORD_RESET.path);
        }}
      >
        <Txt>
          <strong>Click here</strong> if you have <strong>Forgotten Password</strong>
        </Txt>
      </Button>
    ),
  },
};

export type LoginFormConfig = {
  cardProps?: {
    hasCenteredTitle?: boolean;
    hasUppercaseTitle?: boolean;
  };
  title: string;
  introText: string;
  submitButtonText: string;
  verifyButtonText: string;
  forgotPassword: {
    href: string;
    text: string;
  };
  footer: React.ReactNode;
  wrapper?: (children: React.ReactNode) => React.ReactNode;
  loadingOverlay?: React.ReactNode;
};

export type Props = {
  config: LoginFormConfig;
};

function LoginFormBase({ config }: Props) {
  const login = useLogin();
  const [doLogin, loginState] = usePromiseState(login);
  const [loginError, setLoginError] = useState<string | undefined>(undefined);
  const router = useRouter();
  const { error, email: queryEmail } = useQueryStringParams();
  const [prefillEmail, setPrefillEmail] = useState(queryEmail ?? '');
  const [prefillPassword, setPrefillPassword] = useState('');
  const [prefillLoaded, setPrefillLoaded] = useState(!sessionStorage.getItem('login_prefill'));

  useEffect(() => {
    const stored = sessionStorage.getItem('login_prefill');
    if (stored) {
      try {
        const { email: storedEmail, password: storedPassword } = JSON.parse(stored);
        setPrefillEmail(storedEmail ?? '');
        setPrefillPassword(storedPassword ?? '');
      } catch {
      } finally {
        sessionStorage.removeItem('login_prefill');
      }
    }
    setPrefillLoaded(true);
  }, []);
  const [requiresSecondFactor, setRequiresSecondFactor] = useState<string | undefined>(undefined);
  const [secondFactorUsername, setSecondFactorUsername] = useState<string | undefined>(undefined);
  const { startCountdown } = useCountdownTimer('secondFactorCountdown');

  const [allOtpFilled, setAllOtpFilled] = useState(false);

  const { loadRecaptcha, isLoadingRecaptcha } = useRecaptcha('login');

  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const handleTurnstileTokenReceived = useCallback(
    (token: string) => {
      setTurnstileToken(token);
    },
    [setTurnstileToken],
  );

  const handleLogin = async (formData: FormData) => {
    if (
      // username and password not available until
      // conditional rendering on 2fa resolves as 'true'
      requiresSecondFactor &&
      formData.has('2fa_code')
    ) {
      try {
        const recaptchaData = await loadRecaptcha();
        await doLogin(
          formData.get('username')?.toString() ?? '',
          formData.get('password')?.toString() ?? '',
          requiresSecondFactor,
          formData.get('2fa_code')?.toString() ?? '',
          recaptchaData,
          formData.get('overrideCountry')?.toString(),
          turnstileToken || undefined,
        );
        await router.push(DASHBOARD.path, undefined, { shallow: true });
      } catch (err: any) {
        setLoginError(err.message);
      }
    } else if (formData.has('username') && formData.has('password')) {
      try {
        const recaptchaData = await loadRecaptcha();

        await doLogin(
          formData.get('username')?.toString() ?? '',
          formData.get('password')?.toString() ?? '',
          undefined,
          undefined,
          recaptchaData,
          formData.get('overrideCountry')?.toString(),
          turnstileToken || undefined,
        );
        await router.push(DASHBOARD.path, undefined, { shallow: true });
      } catch (err: any) {
        if (err instanceof SecondFactorRequiredError) {
          setSecondFactorUsername(formData.get('username')?.toString() || undefined);
          setRequiresSecondFactor(err.getSessionId());
          startCountdown();
        } else {
          setLoginError(err.message);
        }
      }
    }
  };

  useEffect(() => {
    if (error) {
      setLoginError(decodeURI(error));
    }
  }, [error]);

  const cardContent = (
    <RegistrationCard
      narrowContainer
      hasCenteredTitle={config.cardProps?.hasCenteredTitle}
      hasUppercaseTitle={config.cardProps?.hasUppercaseTitle}
      title={requiresSecondFactor ? '' : config.title}
    >
      {(loginState.isLoading || isLoadingRecaptcha) && !config.loadingOverlay && <LoadingOverlay />}

      {!requiresSecondFactor && (
        <Txt component="p" className={classes.formIntro}>
          {config.introText}
        </Txt>
      )}

      {loginError &&
        (() => {
          const entry = Object.entries(LOGIN_ERROR_MAP).find(([key]) =>
            loginError.toLowerCase().includes(key.toLowerCase()),
          )?.[1];
          return (
            <Alert
              title={entry?.title ?? 'Please try again'}
              subtitle={entry?.message ?? loginError}
              type={'error'}
              action={entry?.action}
              onClose={() => {
                setLoginError(undefined);
              }}
            />
          );
        })()}

      {queryEmail && !requiresSecondFactor && (
        <Alert
          type="success"
          subtitle="We found an account with that email. Enter your password to sign in."
        />
      )}

      <Form
        key={prefillLoaded ? 'ready' : 'loading'}
        submitDisabled={
          loginState.isLoading ||
          isLoadingRecaptcha ||
          (Boolean(requiresSecondFactor) && !allOtpFilled) ||
          (Boolean(process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY) && !turnstileToken)
        }
        submitName={requiresSecondFactor ? config.verifyButtonText : config.submitButtonText}
        buttonWrapperClassName={classes.buttonWrapper}
        onSubmit={handleLogin}
        submitButtonType="primary"
        className={classes.formOverrides}
        submitButtonClassName={classes.submitButton}
      >
        {!requiresSecondFactor && (
          <>
            <UsernameInput defaultValue={prefillEmail} />

            <PasswordInput
              id={'password'}
              title={'Password'}
              placeholder={'Enter password'}
              required
              autoComplete={'current-password'}
              defaultValue={prefillPassword}
              overrideErrorMessage={(_validity, _value) => {
                return PASSWORD_REQUIREMENT_DESCRIPTIONS;
              }}
            />

            <Button
              buttonType="text"
              component={A}
              href={config.forgotPassword.href}
              className={classes.forgotPassword}
              color="primary"
            >
              <Txt>{config.forgotPassword.text}</Txt>
            </Button>

            <TurnstileWidget onTokenReceived={handleTurnstileTokenReceived} action="login" />
          </>
        )}

        {requiresSecondFactor && (
          <SecondFactorCode
            username={secondFactorUsername || ''}
            returnHref="/"
            actionText="Retry login"
            initialText="Retry login in "
            onAllInputsFilled={setAllOtpFilled}
          />
        )}
      </Form>

      {config.footer}
    </RegistrationCard>
  );

  const isLoading = loginState.isLoading || isLoadingRecaptcha;

  if (config.wrapper) {
    return (
      <>
        {config.wrapper(cardContent)}
        {isLoading && config.loadingOverlay}
      </>
    );
  }

  return (
    <>
      {cardContent}
      {isLoading && config.loadingOverlay}
    </>
  );
}

export default function LoginFormBaseWrapper(props: Props) {
  return (
    <RecaptchaProvider>
      <LoginFormBase {...props} />
    </RecaptchaProvider>
  );
}
